/**
 * WOW · measure: typed funnel analytics, offline-first.
 *
 *  - `track(name, props)` records a typed event (core FUNNEL_EVENT_NAMES) into a persisted buffer.
 *  - The app's existing descriptive log (store.logEvent) is tapped and mapped (core mapLegacyEvent),
 *    so screens keep logging the way they always did.
 *  - The buffer is uploaded in batches to POST /api/v1/events when online, the live config allows it
 *    (`analyticsUpload`) and a grown-up allowed analytics; refused consent clears it.
 *  - Also keeps a tiny per-profile diary (minutes in the app, lessons, photos per day) and a weekly
 *    skill snapshot for the parents' weekly report.
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  EVENTS_MAX_BATCH,
  ackEvents,
  dayKey,
  daysBetween,
  enqueueEvent,
  isFunnelEventName,
  SERVER_EVENT_NAMES,
  withSuperProps,
  mapLegacyEvent,
  retentionMarkers,
  sanitizeProps,
  weekKey,
  type DiaryDay,
  type PerfSummary,
  type EventProps,
  type SuperProps,
  type FunnelEventName,
  type SkillId,
  type TrackedEvent,
} from '@sketchling/core';
import { APP_VERSION, appNow, uid, useApp, type AnalyticsEvent } from '../state/store';
import { API_ORIGIN } from './api';
import { platform } from './platform';

interface AnalyticsState {
  installId: string;
  buffer: TrackedEvent[];
  /** One-off markers already recorded (return_d1, return_d7, first_drawing_done). */
  markers: string[];
  /** profileId → day key → activity. */
  diary: Record<string, Record<string, DiaryDay>>;
  /** profileId → skill XP at the start of the week `week`. */
  skillSnap: Record<string, { week: string; xp: Partial<Record<SkillId, number>> }>;
  lastFlushAt: string | null;
  lastFlushError: string | null;
}

const newInstallId = () => `i${uid()}${uid()}`.replace(/[^A-Za-z0-9]/g, '').slice(0, 32);

const storage = createJSONStorage(() => ({
  getItem: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  setItem: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* quota / private mode */
    }
  },
  removeItem: (k: string) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
}));

export const useAnalytics = create<AnalyticsState>()(
  persist(
    () => ({
      installId: newInstallId(),
      buffer: [] as TrackedEvent[],
      markers: [] as string[],
      diary: {} as Record<string, Record<string, DiaryDay>>,
      skillSnap: {} as AnalyticsState['skillSnap'],
      lastFlushAt: null as string | null,
      lastFlushError: null as string | null,
    }),
    { name: 'sketchling-analytics-v1', storage },
  ),
);

export const EMPTY_DIARY: Record<string, DiaryDay> = {};

/* ---------------------------------- listeners ---------------------------------- */

type TrackListener = (ev: TrackedEvent) => void;
type LegacyListener = (name: string, props: Record<string, unknown>) => void;
const trackListeners = new Set<TrackListener>();
const legacyListeners = new Set<LegacyListener>();
/** Called for every typed event (Juno reactions, dev overlays). */
export const onTrack = (fn: TrackListener) => (trackListeners.add(fn), () => void trackListeners.delete(fn));
/** Called for every descriptive app event (store.logEvent) after start. */
export const onAppEvent = (fn: LegacyListener) => (legacyListeners.add(fn), () => void legacyListeners.delete(fn));

/* ------------------------------------ track ------------------------------------ */

/** Super-props on every event (MONETIZATION-PLAN §11.3): plan, trial, period, free boundary, install age, buckets. */
function superProps(): SuperProps {
  const st = useApp.getState();
  const e = st.entitlement;
  const buckets = Object.entries(st.buckets ?? {})
    .map(([k, v]) => `${k}:${v}`)
    .join(',')
    .slice(0, 64);
  return {
    tier: e.tier,
    isTrial: !!e.trialEndsAt && Date.parse(e.trialEndsAt) > Date.now(),
    period: e.period ?? null,
    freeBoundary: st.config.freeBoundary,
    daysSinceInstall: daysBetween(st.firstLaunch, appNow()),
    ...(buckets ? { buckets } : {}),
  };
}

export function track(name: FunnelEventName, props?: Record<string, unknown>): TrackedEvent {
  let merged: EventProps | undefined;
  try {
    merged = withSuperProps(props, superProps());
  } catch {
    merged = sanitizeProps(props);
  }
  const ev: TrackedEvent = { id: uid(), name, at: appNow().toISOString(), ...(merged ? { props: merged } : {}) };
  useAnalytics.setState((s) => ({ buffer: enqueueEvent(s.buffer, ev) }));
  for (const fn of trackListeners) {
    try {
      fn(ev);
    } catch {
      /* listener errors never break tracking */
    }
  }
  return ev;
}

/**
 * Monetization events (MONETIZATION-PLAN §11.3: paywall_close, parental_gate_*, wish_add, invite_*, …).
 * Typed when core FUNNEL_EVENT_NAMES knows the name; otherwise it stays in the descriptive log
 * (`money:<name>`) so an older core never makes the upload batch fail validation.
 */
export function trackMoney(name: string, props?: Record<string, unknown>): void {
  // Lifecycle events (trial_start, renewal, …) are written by the server only.
  if (isFunnelEventName(name) && !SERVER_EVENT_NAMES.includes(name)) track(name, props);
  else useApp.getState().logEvent(`money:${name}`, props);
}

/** Record a one-off funnel event once per install (first_drawing_done, return_d1, ...). */
export function trackOnce(name: FunnelEventName, props?: Record<string, unknown>): boolean {
  if (useAnalytics.getState().markers.includes(name)) return false;
  useAnalytics.setState((s) => ({ markers: [...s.markers, name] }));
  track(name, props);
  return true;
}

/* ------------------------------------ diary ------------------------------------ */

function bumpDiary(profileId: string | null, patch: Partial<DiaryDay>) {
  if (!profileId) return;
  const k = dayKey(appNow());
  useAnalytics.setState((s) => {
    const days = s.diary[profileId] ?? {};
    const cur = days[k] ?? { sec: 0, lessons: 0, captures: 0 };
    const next = { sec: cur.sec + (patch.sec ?? 0), lessons: cur.lessons + (patch.lessons ?? 0), captures: cur.captures + (patch.captures ?? 0) };
    // Keep ~10 weeks per profile.
    const kept = Object.fromEntries(Object.entries({ ...days, [k]: next }).sort(([a], [b]) => (a < b ? -1 : 1)).slice(-70));
    return { diary: { ...s.diary, [profileId]: kept } };
  });
}

/** Snapshot each profile's skill XP the first time we see it in a new week (baseline for "skills grown"). */
function snapshotSkills() {
  const st = useApp.getState();
  const wk = weekKey(appNow());
  const snaps = useAnalytics.getState().skillSnap;
  const add: AnalyticsState['skillSnap'] = {};
  for (const p of st.profiles) {
    if (snaps[p.id]?.week === wk) continue;
    add[p.id] = { week: wk, xp: { ...(st.progress[p.id]?.skillXp ?? {}) } };
  }
  if (Object.keys(add).length) useAnalytics.setState((s) => ({ skillSnap: { ...s.skillSnap, ...add } }));
}

/* ------------------------------------ upload ------------------------------------ */

let flushing = false;

export async function flushEvents(): Promise<number> {
  if (flushing) return 0;
  const app = useApp.getState();
  const { buffer, installId } = useAnalytics.getState();
  if (!buffer.length) return 0;
  if (!app.onboarding.analyticsConsent) {
    // A grown-up said no: nothing leaves the device, and nothing is kept for later.
    if (app.onboarding.done) useAnalytics.setState({ buffer: [] });
    return 0;
  }
  if (app.config.analyticsUpload === false) return 0;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 0;
  flushing = true;
  let sent = 0;
  try {
    for (let i = 0; i < 5; i++) {
      const batch = useAnalytics.getState().buffer.slice(0, EVENTS_MAX_BATCH);
      if (!batch.length) break;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = useApp.getState().account.accessToken;
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}/api/v1/events`, {
        method: 'POST',
        headers,
        keepalive: true,
        body: JSON.stringify({ installId, appVersion: APP_VERSION, platform, lang: app.settings.language, events: batch }),
      });
      if (res.status === 400) {
        // A malformed event would block the queue forever: drop this batch.
        useAnalytics.setState((s) => ({ buffer: ackEvents(s.buffer, batch.map((e) => e.id)), lastFlushError: 'rejected' }));
        continue;
      }
      if (!res.ok) throw new Error(String(res.status));
      const j = (await res.json()) as { accepted: string[] };
      useAnalytics.setState((s) => ({ buffer: ackEvents(s.buffer, j.accepted), lastFlushAt: new Date().toISOString(), lastFlushError: null }));
      sent += j.accepted.length;
      if (batch.length < EVENTS_MAX_BATCH) break;
    }
  } catch (e) {
    useAnalytics.setState({ lastFlushError: e instanceof Error ? e.message : 'offline' });
  } finally {
    flushing = false;
  }
  return sent;
}

/* ------------------------------------ start ------------------------------------ */

let started = false;

function handleAppEvent(e: AnalyticsEvent) {
  const props = e.props ?? {};
  for (const m of mapLegacyEvent(e.name, props)) track(m.name, m.props);
  const st = useApp.getState();
  const pid = st.activeProfileId;
  if (e.name === 'Unit Completed') {
    bumpDiary(pid, { lessons: 1 });
    trackOnce('first_drawing_done', { via: 'lesson' });
  }
  if (e.name === 'Journal Item Added' && props.source !== 'calibration') trackOnce('first_drawing_done', { via: String(props.source ?? 'journal') });
  if (e.name === 'Drawing Photo Uploaded') bumpDiary(pid, { captures: 1 });
  for (const fn of legacyListeners) {
    try {
      fn(e.name, props);
    } catch {
      /* ignore */
    }
  }
}

/** Boot once (WowHost): app_open + retention markers, log tap, time diary, periodic upload. */
export function startAnalytics(): () => void {
  if (started) return () => undefined;
  started = true;
  const startedAt = Date.now() - 1000;
  const st = useApp.getState();
  const now = appNow();
  track('app_open', { day: daysBetween(st.firstLaunch, now), platform });
  for (const m of retentionMarkers(st.firstLaunch, now, useAnalytics.getState().markers)) trackOnce(m);
  snapshotSkills();

  // Tap the descriptive log stream (only events logged after start: hydration replays are ignored).
  const unsub = useApp.subscribe((s, prev) => {
    if (s.analytics === prev.analytics) return;
    const last = prev.analytics[prev.analytics.length - 1];
    let from = last ? s.analytics.lastIndexOf(last) + 1 : 0;
    if (from <= 0 && last) from = Math.max(0, s.analytics.length - 1);
    for (const e of s.analytics.slice(from)) if (Date.parse(e.at) >= startedAt) handleAppEvent(e);
    if (s.profiles !== prev.profiles) snapshotSkills();
  });

  // Foreground time with the active profile (15 s ticks).
  const tick = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    bumpDiary(useApp.getState().activeProfileId, { sec: 15 });
  }, 15_000);
  const flushTimer = setInterval(() => void flushEvents(), 30_000);
  const onOnline = () => void flushEvents();
  const onHide = () => document.visibilityState === 'hidden' && void flushEvents();
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onHide);
  setTimeout(() => void flushEvents(), 4000);
  return () => {
    unsub();
    clearInterval(tick);
    clearInterval(flushTimer);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onHide);
    started = false;
  };
}

/* --------------------------------- dashboard --------------------------------- */

export interface DashboardData {
  since: string;
  days: number;
  totals: { events: number; installs: number };
  funnel: { step: FunnelEventName; installs: number; ofStart: number; ofPrev: number }[];
  retention: { cohorts: { day: string; installs: number; d1: number; d7: number }[]; d1Rate: number | null; d7Rate: number | null; d1Eligible: number; d7Eligible: number };
  daily: { day: string; installs: number }[];
  byName: { name: string; count: number }[];
  onboarding: { step: string; installs: number }[];
  perf?: PerfSummary;
}

export async function fetchDashboard(days = 30): Promise<DashboardData> {
  const res = await fetch(`${API_ORIGIN}/api/v1/events/dashboard?days=${days}`);
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as DashboardData;
}
